import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { ReactNode } from "react";

import { AddClientContext, AddClientContextType } from "./AddClientContext";

import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { instance } from "@/lib/axios/instance";
import { AddClientRequest } from "@/types/request/AddClientRequest";
import {
  AddClientErrorResponse,
  AddClientResponse,
} from "@/types/response/AddClientResponse";

type AddClientProviderProps = {
  children: ReactNode | ((context: AddClientContextType) => ReactNode);
  tenantId?: string | null;
};

export const AddClientProvider = ({
  children,
  tenantId,
}: AddClientProviderProps) => {
  const queryClient = useQueryClient();

  // Add client mutation
  const addClient: UseMutationResult<
    AxiosResponse<AddClientResponse>,
    AxiosError<AddClientErrorResponse>,
    AddClientRequest
  > = useMutation({
    mutationFn: (addClientRequest: AddClientRequest) =>
      instance.post("/api/v2/Clients", { ...addClientRequest, tenantId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientQueryKeys.all,
      });
    },
  });

  const value = {
    addClientError: addClient.failureReason?.response?.data,
    addClient,
    isPending: addClient.isPending,
    reset: addClient.reset,
  };

  return (
    <AddClientContext.Provider value={value}>
      {typeof children === "function" ? children(value) : children}
    </AddClientContext.Provider>
  );
};
